package br.com.texeratx.boardgametimeapp.ui.screens.myclans

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.session.SessionManager
import br.com.texeratx.boardgametimeapp.domain.usecases.login.ForgotPasswordUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.login.ResetPasswordUseCase
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class ChangePasswordUiState(
    val email: String = "",
    val isSendingCode: Boolean = false,
    val codeSent: Boolean = false,
    val code: String = "",
    val newPassword: String = "",
    val confirmPassword: String = "",
    val isSubmitting: Boolean = false,
    val error: String? = null,
)

class ChangePasswordViewModel(
    private val sessionManager: SessionManager,
    private val forgotPasswordUseCase: ForgotPasswordUseCase,
    private val resetPasswordUseCase: ResetPasswordUseCase,
) : ScreenModel {

    private val _state = MutableStateFlow(ChangePasswordUiState())
    val state = _state.asStateFlow()

    fun sendCode() {
        screenModelScope.launch {
            val email = sessionManager.load()?.email ?: return@launch
            _state.value = _state.value.copy(email = email, isSendingCode = true, error = null)
            forgotPasswordUseCase(email).fold(
                onSuccess = {
                    _state.value = _state.value.copy(isSendingCode = false, codeSent = true)
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isSendingCode = false,
                        error = e.message ?: "Erro ao enviar código"
                    )
                }
            )
        }
    }

    fun updateCode(v: String) {
        _state.value = _state.value.copy(code = v.filter { it.isDigit() }.take(6), error = null)
    }

    fun updateNewPassword(v: String) {
        _state.value = _state.value.copy(newPassword = v, error = null)
    }

    fun updateConfirmPassword(v: String) {
        _state.value = _state.value.copy(confirmPassword = v, error = null)
    }

    fun submit(onSuccess: () -> Unit) {
        val s = _state.value
        if (s.code.length != 6) {
            _state.value = s.copy(error = "O código deve ter 6 dígitos")
            return
        }
        if (s.newPassword.length < 6) {
            _state.value = s.copy(error = "A nova senha deve ter pelo menos 6 caracteres")
            return
        }
        if (s.newPassword != s.confirmPassword) {
            _state.value = s.copy(error = "As senhas não coincidem")
            return
        }
        screenModelScope.launch {
            _state.value = s.copy(isSubmitting = true, error = null)
            resetPasswordUseCase(s.email, s.code, s.newPassword).fold(
                onSuccess = {
                    _state.value = _state.value.copy(isSubmitting = false)
                    onSuccess()
                },
                onFailure = { e ->
                    _state.value = _state.value.copy(
                        isSubmitting = false,
                        error = e.message ?: "Código inválido ou expirado"
                    )
                }
            )
        }
    }
}

class ChangePasswordScreen : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val vm = getScreenModel<ChangePasswordViewModel>()
        val state by vm.state.collectAsState()
        // navigator is needed to pop; get it inside a local composable
        val navigator = cafe.adriel.voyager.navigator.LocalNavigator.currentOrThrow

        LaunchedEffect(Unit) { vm.sendCode() }

        var showNew by remember { mutableStateOf(false) }
        var showConfirm by remember { mutableStateOf(false) }

        AppScreenScaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Trocar senha", fontWeight = FontWeight.Bold) },
                    navigationIcon = {
                        IconButton(onClick = { navigator.pop() }) {
                            Icon(Icons.Default.ArrowBack, contentDescription = "Voltar")
                        }
                    },
                    colors = AppTopBarColors()
                )
            }
        ) { padding ->
            Box(
                Modifier
                    .padding(padding)
                    .fillMaxSize()
            ) {
                when {
                    state.isSendingCode -> {
                        Column(
                            Modifier.align(Alignment.Center),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            CircularProgressIndicator()
                            Text("Enviando código para seu e-mail...")
                        }
                    }

                    !state.codeSent -> {
                        // falha ao enviar: mostrar erro + botão de tentar novamente
                        Column(
                            Modifier
                                .fillMaxSize()
                                .padding(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            state.error?.let {
                                Text(it, color = MaterialTheme.colorScheme.error)
                            }
                            Button(
                                onClick = { vm.sendCode() },
                                modifier = Modifier.fillMaxWidth()
                            ) { Text("Tentar novamente") }
                        }
                    }

                    else -> {
                        Column(
                            Modifier
                                .fillMaxSize()
                                .padding(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Text(
                                "Enviamos um código de verificação para ${state.email}. " +
                                    "Insira o código abaixo junto com sua nova senha.",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )

                            OutlinedTextField(
                                value = state.code,
                                onValueChange = vm::updateCode,
                                label = { Text("Código de 6 dígitos") },
                                placeholder = { Text("000000") },
                                singleLine = true,
                                modifier = Modifier.fillMaxWidth()
                            )

                            OutlinedTextField(
                                value = state.newPassword,
                                onValueChange = vm::updateNewPassword,
                                label = { Text("Nova senha") },
                                singleLine = true,
                                visualTransformation = if (showNew) VisualTransformation.None else PasswordVisualTransformation(),
                                trailingIcon = {
                                    IconButton(onClick = { showNew = !showNew }) {
                                        Icon(
                                            if (showNew) Icons.Default.VisibilityOff else Icons.Default.Visibility,
                                            contentDescription = null
                                        )
                                    }
                                },
                                modifier = Modifier.fillMaxWidth()
                            )

                            OutlinedTextField(
                                value = state.confirmPassword,
                                onValueChange = vm::updateConfirmPassword,
                                label = { Text("Confirmar nova senha") },
                                singleLine = true,
                                visualTransformation = if (showConfirm) VisualTransformation.None else PasswordVisualTransformation(),
                                trailingIcon = {
                                    IconButton(onClick = { showConfirm = !showConfirm }) {
                                        Icon(
                                            if (showConfirm) Icons.Default.VisibilityOff else Icons.Default.Visibility,
                                            contentDescription = null
                                        )
                                    }
                                },
                                modifier = Modifier.fillMaxWidth()
                            )

                            state.error?.let {
                                Text(it, color = MaterialTheme.colorScheme.error)
                            }

                            Button(
                                onClick = { vm.submit { navigator.pop() } },
                                enabled = !state.isSubmitting &&
                                    state.code.length == 6 &&
                                    state.newPassword.isNotBlank() &&
                                    state.confirmPassword.isNotBlank(),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                if (state.isSubmitting) {
                                    CircularProgressIndicator(
                                        color = MaterialTheme.colorScheme.onPrimary,
                                        modifier = Modifier.size(20.dp)
                                    )
                                } else {
                                    Text("Confirmar nova senha")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
